import { useRouter } from 'next/router'
import { useRef, useState } from 'react'

import { assetPrefix } from 'config/env_config'
import { actionTypes, useServiceScheduleContext } from '../../context/serviceScheduleContext'
import Button from 'src/components/button'
import EditableInfo from 'src/components/editableInfo'
import { getSchedulingInfoConfig } from './schedulingInfoConfig'
import { getPurchasedPackageSchedulingInfoConfig } from './purchasedPackageSchedulingInfoConfig'
import ChevronDown from 'static/icons/chevron-down.svg'
import ChevronUp from 'static/icons/chevron-up.svg'
import { formatPrice, getPetPhotoByType } from 'utils'

import * as S from './styles'

const SchedulingReview = ({ schedule, current, previousState }) => {
  const elementRef = useRef()
  const router = useRouter()
  const [expanded, setExpanded] = useState(!!current)
  const {
    state: schedulingState,
    dispatch,
    steps,
    functions,
    editSteps
  } = useServiceScheduleContext()

  const stateToRender = previousState?.data || schedulingState

  const {
    pricesInfo,
    handleEditMultipleScheduling
  } = functions

  const {
    serviceData,
    petsData,
    additionalServiceData,
    partnersData,
    professionalData,
    formList,
    schedulingItemError,
    invertStep,
    obs,
    selectedPurchasedPackage
  } = stateToRender

  const {
    currentSchedulePrice,
    currentPriceWithDiscount,
    currentHasDiscount
  } = pricesInfo()

  const schedulingHasDiscount = current ? currentHasDiscount : previousState?.scheduling?.discountedPrice < previousState?.scheduling?.price
  const schedulingPrice = current ? currentSchedulePrice : previousState?.scheduling?.price
  const schedulingPriceWithDiscount = current ? currentPriceWithDiscount : previousState?.scheduling?.discountedPrice
  const professionalList = professionalData || []
  const selectedPet = petsData.find(pet => pet.id === schedule?.pet_id)
  const selectedService = serviceData.find(service => service.value === schedule?.service_id)
  const selectedAdditionalServices = additionalServiceData.filter(additionalService => schedule?.additional_services?.map(ad => ad.id).includes(additionalService.value))
  const selectedPartner = partnersData?.find(partner => partner.id === schedule?.partner_id)
  const selectedProfessional = professionalList?.find(professional => professional.id === schedule?.professional_id)
  const selectedDay = schedule?.selectDay
  const selectedTime = schedule?.selectTime
  const serviceIndex = schedule?.serviceIndex

  const currentSchedulingInfo = schedule?.isSchedulingFromPackage
    ? getPurchasedPackageSchedulingInfoConfig({
      selectedService,
      selectedAdditionalServices,
      selectedPartner: selectedPartner || selectedPurchasedPackage?.partner,
      selectedProfessional,
      selectedDay,
      selectedTime,
      obs,
      invertStep,
      selectedPurchasedPackage,
      hasSelectedPartner: selectedPartner,
      serviceIndex
    }, schedulingState?.isEditingScheduling)
    : getSchedulingInfoConfig({
      selectedService,
      selectedAdditionalServices,
      selectedPartner,
      selectedProfessional,
      selectedDay,
      selectedTime,
      obs,
      invertStep
    })

  const onGoTo = path => {
    router.push({
      pathname: `/agendamentos/${path}`,
      query: router.query
    })
  }

  const getPetActionOption = () => {
    if (schedule?.isSchedulingFromPackage) return

    return (
      current ? <S.Edit src={`${assetPrefix}/static/icons/pen-outline.svg`} alt='Editar ítem' /> : Chevron[expanded]

    )
  }

  const editScheduling = (step = 0) => {
    const redirectedStep = steps.find(schedulingStep => schedulingStep.step === step)

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...editSteps()[redirectedStep?.id || 0],
        formList,
        review: false,
        isHowToPay: false
      }
    }, () => {
      const stepUrl = steps.find(schedulingStep => schedulingStep.step === (step || 1))

      onGoTo(stepUrl.url)
    })
  }

  const handleExpand = () => {
    setExpanded(!expanded)
  }

  const Chevron = {
    true: <ChevronUp />,
    false: <ChevronDown />
  }

  return (
    <S.StickyCard
      onClick={() => {
        if (!current) handleExpand()
      }} expanded={expanded}
    >
      <S.Container ref={elementRef}>
        <S.InfoCard
          active
          marginBottom={0}
          onClick={() => {
            if (current && !schedule?.isSchedulingFromPackage) editScheduling()
          }}
          expanded={expanded}
        >
          <S.InfoCardContent expanded={expanded}>
            <S.Photo
              src={selectedPet?.photo || getPetPhotoByType(selectedPet?.breed?.type)}
              alt={selectedPet?.name}
              setDefaultPhoto={!selectedPet?.photo}
            />
            <S.Summary>
              <S.Pet title={selectedPet?.name}>
                <p className='pet-name'>{selectedPet?.name}</p>
                <p className='pet-breed-name'>{selectedPet?.breed?.name}</p>
                {!!schedulingItemError && (
                  <S.WarningStatus>
                    <img src={`${assetPrefix}/static/icons/warning-status.svg`} alt='Erro' />
                  </S.WarningStatus>
                )}
              </S.Pet>

            </S.Summary>
            {getPetActionOption()}
          </S.InfoCardContent>
        </S.InfoCard>
        <S.ExpandedInfo expanded={expanded}>
          <h2>Informações principais</h2>
          <S.CurrentSchedule expanded={expanded} noMarginBottom={schedule?.isSchedulingFromPackage} noBorderBottom={schedule?.isSchedulingFromPackage}>
            {currentSchedulingInfo.sort((a, b) => a.fieldType === 'observations' ? 1 : a.step - b.step).map((schedule, i) => {
              if (schedule.infoTitle) {
                return (
                  <EditableInfo
                    noEdit={!current || schedule.noEdit}
                    key={i}
                    icon={schedule.infoIcon}
                    iconAlt={schedule.stepName}
                    iconSize={24}
                    title={schedule.infoTitle}
                    additionalInfo={schedule.infoAdditional}
                    onEdit={() => editScheduling(schedule.step)}
                    isObs={schedule.fieldType === 'observations'}
                  />
                )
              }
            })}
          </S.CurrentSchedule>
          <S.WrapperPricesAndAnotherSchedulings>
            {!schedule?.isSchedulingFromPackage && (
              <S.CalcPrice expanded={expanded}>
                <img src={`${assetPrefix}/static/icons/money-outline.svg`} />
                <div>{(formList.length || !current) ? 'Subtotal' : 'Total'}</div>
                <S.Total>
                  <>
                    {schedulingHasDiscount && <S.Total previousPrice>{formatPrice(schedulingPrice)}</S.Total>}
                    {schedulingHasDiscount ? formatPrice(schedulingPriceWithDiscount) : formatPrice(schedulingPrice)}
                  </>
                </S.Total>
              </S.CalcPrice>
            )}
            {!current && <Button onClick={() => handleEditMultipleScheduling(previousState)} margin='16px 0 0 0' theme='secondary' data-test='edit-scheduling'>Editar agendamento</Button>}
          </S.WrapperPricesAndAnotherSchedulings>
        </S.ExpandedInfo>
      </S.Container>
    </S.StickyCard>
  )
}

export default SchedulingReview
